import {DatePicker, Form, Input, Modal, Switch} from "antd";
import React, {useRef} from "react";
import {IPolicyFormData, IPolicyModalState, PolicyCreateModalProps} from "../types/policies.types";
import server from "../../../server";
import {getMetaFields} from "../action/policy-actions";
import {
    getMetaValuesFromUI, getSystemFieldsValue,
    getSystemMetaFields,
    getUIMetaFields
} from "../../common/upload-document-modal/utils/document-utils";
import {MetaPayload} from "../../../domains/models/document";
import {buildFormElements} from "../../common/upload-document-modal/utils/buildMetaFormELements";
import {ChevronUp, FileCheck} from "lucide-react";
import {useBaseComponent} from "../../../base/base-component";
import {message} from "antd/lib";
import Locale from "../../../core/locale/locale";
import DocumentUploader from "../../common/document-uploader/document-uploader";
import TiptapEditor from "../../common/tiptap-editor/tiptap-editor";


const PolicyCreateModal = (props: PolicyCreateModalProps) => {

    const [form] = Form.useForm();
    const [metaForm] = Form.useForm();
    const documentRef = useRef<any>(null);
    const {state, updateState} = useBaseComponent<PolicyCreateModalProps, IPolicyModalState>(props, {
        initialState: () => ({
            internal: {
                systemFields: [],
                uiFields: [],
                metaValues: {},
                showMetaForm: false,
            }
        }),
        onInit: () => onInit()
    })


    async function onInit() {
        const metaRes = await getMetaFields();
        if (metaRes && metaRes.length > 0) {
            const systemFields = getSystemMetaFields(metaRes);
            const uiFields = getUIMetaFields(metaRes);
            updateState('internal.uiFields', uiFields);
            updateState('internal.systemFields', systemFields);
        }
    }

    const saveAsDraft = Form.useWatch("saveAsDraft", form);
    const notifyEmployees = Form.useWatch("notifyEmployees", form);

    async function handleMetaFormSubmit() {
        const formValues = metaForm.getFieldsValue();

        let uiMeta: MetaPayload[] = [];
        let systemMeta: MetaPayload[] = [];

        if (state.internal.uiFields) {
            uiMeta = getMetaValuesFromUI(state.internal.uiFields, formValues);
        }

        if (state.internal.systemFields) {
            systemMeta = getSystemFieldsValue(state.internal.systemFields, formValues);
        }

        //merge both
        const combinedMeta: MetaPayload[] = [...systemMeta, ...uiMeta];

        //  convert to required format
        const finalMetaData: Record<number, MetaPayload[]> = {
            0: combinedMeta
        };

        updateState('internal.metaValues', finalMetaData);
    }

    async function handleFormFinish() {

        const formValues = form.getFieldsValue();
        let documentId = formValues.documentId;
        //If no documentId → upload now
        if (!documentId && documentRef.current) {
            documentId = await documentRef.current.uploadDocument(state.internal.metaValues);

            if (!documentId) {
                message.error("Please upload a document");
                return;
            }
        }
        //Inject documentId into formField
        form.setFieldValue('documentId', documentId);


        //validateFormFields
        await form.validateFields()
            .then((formValues: IPolicyFormData) => {
                props.onSubmit(formValues);
                props.setOpen(false);
                form.resetFields();
            })
            .catch(() => console.error("Error while submitting form"));
    }

    const onCancel = async () => {
        const documentId = form.getFieldValue('documentId');
        if (documentId) {
            try {
                await server.document.delete(documentId)
            } catch (error) {
                console.error("Failed to delete document:", error);
            }
        }
        props.setOpen(false);
        form.resetFields();
    };


    function toggleMetaFormView() {
        updateState('internal.showMetaForm', !state.internal.showMetaForm);
    }

    return (
        <Modal
            className="hrms-app policy-modal"
            title="Upload Policy"
            open={props.open}
            onOk={handleFormFinish}
            onCancel={onCancel}
            okText="Submit"
        >
            <Form form={form} layout="vertical" className="policy-form">
                <Form.Item
                    label="Policy Title"
                    name="title"
                    required
                    rules={[
                        {
                            required: true,
                            message: "Please enter Policy Title",
                        }
                    ]}>
                    <div className="policy-title-container">
                        <Input
                            className="policy-title-input"
                            placeholder="Enter Policy Title"
                        />
                    </div>
                </Form.Item>
                <Form.Item
                    label="Description"
                    name="description"
                    className="policy-description"
                    required
                    rules={[
                        {
                            required: true,
                            message: "Please enter Policy Description",
                        }
                    ]}
                >
                    <TiptapEditor/>
                </Form.Item>

                <div className={'policy-modal__policy-date-wrapper'}>
                    <Form.Item
                        label="Applicable From"
                        name="applicableFrom"
                        required
                        rules={[
                            {
                                required: true,
                                message: "Please enter Date",
                            }
                        ]}
                    >
                        <DatePicker
                            placeholder="Select Date"
                            className="policy-date-input"
                            format={Locale.displayDate}
                        />
                    </Form.Item>
                    <Form.Item
                        label={"Policy Document"}
                        name={"documentId"}
                        rules={[
                            {
                                required: true,
                                message: 'Please upload the the document',
                            },
                        ]}
                    >
                        <DocumentUploader
                            uploadButtonText={'Upload Policy'}
                            ref={documentRef}
                            docType={'POLICY'}
                            disabled={false}
                            uploadMode={'onSubmit'}
                        />
                    </Form.Item>
                </div>
                <div className={'policy-modal__policy-date-wrapper'}>
                    <Form.Item
                        label="Save as Draft?"
                        name="saveAsDraft"
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            disabled={notifyEmployees === true}
                            onChange={(checked) => {
                                if (checked) {
                                    form.setFieldValue("notifyEmployees", false);
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Notify employees via email?"
                        name="notifyEmployees"
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            disabled={saveAsDraft === true}
                            onChange={(checked) => {
                                if (checked) {
                                    form.setFieldValue("saveAsDraft", false);
                                }
                            }}
                        />
                    </Form.Item>
                </div>
            </Form>
            {state.internal.uiFields && state.internal.uiFields.length > 0 &&
            <div className={`file-meta ${state.internal.showMetaForm ? 'active' : ''}`}>
                <div
                    className={'file-meta__detail-wrapper'}
                    onClick={() => toggleMetaFormView()}>
                        <span className={'file-meta__icon-wrapper'}>
                           <FileCheck className="file-meta__icon file-icon"/>
                        </span>
                    <span className={'file-meta__text'}>Add Meta Information</span>
                    <span className={'file-meta__tag'}>
                            Optional
                        </span>
                    <ChevronUp
                        onClick={() => toggleMetaFormView()}
                        className={`file-meta__icon dropdown-icon ${!state.internal.showMetaForm ? 'down' : 'up'}`}/>

                </div>
                {state.internal.showMetaForm && (
                    <div className={`file-meta__form ${state.internal.showMetaForm ? 'show' : 'hide'}`}>
                        <Form form={metaForm}>
                            {buildFormElements(state.internal.uiFields ?? [])}
                            <button className={'submit-btn'} onClick={handleMetaFormSubmit}>Submit</button>
                        </Form>

                    </div>
                )
                }
            </div>}

        </Modal>
    )
}

export default PolicyCreateModal;
